import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  GraduationCap, 
  Users,
  ArrowLeft,
  AlertCircle,
  School,
  BookOpen
} from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loginWithGoogle, isAuthenticated, user } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [role, setRole] = useState('student'); // 'student' | 'parent'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    school: '',
    year_group: '',
    exam_board: '',
    tier: '',
    student_email: '', // For parents
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;
      if (mode === 'login') {
        result = await login(formData.email, formData.password);
      } else {
        result = await register({
          ...formData,
          role,
        });
      }

      if (result.success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle(role, role === 'parent' ? formData.student_email : null);
  };

  return (
    <div className="min-h-screen bg-[#FFFCF5] relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 text-[200px] font-serif text-[#0F172A]">π</div>
        <div className="absolute bottom-20 right-20 text-[200px] font-serif text-[#0F172A]">∑</div>
        <div className="absolute top-1/2 left-1/3 text-[150px] font-serif text-[#0F172A]">∫</div>
      </div>

      {/* Back to Home */}
      <div className="absolute top-6 left-6 z-10">
        <Link 
          to="/" 
          className="flex items-center gap-2 text-[#0F172A] hover:text-[#C5A059] transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Home</span>
        </Link>
      </div>

      <div className="flex min-h-screen">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] p-12 flex-col justify-between relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 text-[300px] font-serif text-white">∞</div>
          </div>
          
          <div className="relative z-10">
            <h1 className="font-['Playfair_Display'] text-4xl font-bold text-white mb-4">
              Abba Academy
            </h1>
            <p className="text-white/70 text-lg">Student & Parent Portal</p>
          </div>

          <div className="relative z-10 space-y-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-[#C5A059]" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Track Progress</h3>
                <p className="text-white/60 text-sm">Upload RAG reports and monitor academic improvement over time.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-[#C5A059]" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Parent Access</h3>
                <p className="text-white/60 text-sm">Parents can view their child's progress and stay informed.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-[#C5A059]" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Expert Support</h3>
                <p className="text-white/60 text-sm">Direct messaging with Dr. Michael via WhatsApp.</p>
              </div>
            </div>
          </div>

          <div className="relative z-10">
            <p className="text-white/40 text-sm">© 2026 Abba Academy Limited. All rights reserved.</p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">
                Abba Academy
              </h1>
              <p className="text-[#64748B]">Student & Parent Portal</p>
            </div>

            {/* Mode Toggle */}
            <div className="flex bg-[#F1F5F9] rounded-lg p-1 mb-8">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === 'login' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-[#64748B] hover:text-[#0F172A]'
                }`}
                data-testid="login-tab"
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === 'register' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-[#64748B] hover:text-[#0F172A]'
                }`}
                data-testid="register-tab"
              >
                Create Account
              </button>
            </div>

            {/* Role Selection (Register only) */}
            {mode === 'register' && (
              <div className="mb-6">
                <Label className="text-[#0F172A] mb-3 block">I am a:</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setRole('student')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      role === 'student'
                        ? 'border-[#C5A059] bg-[#C5A059]/5'
                        : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                    }`}
                    data-testid="role-student"
                  >
                    <GraduationCap className={`w-8 h-8 mx-auto mb-2 ${
                      role === 'student' ? 'text-[#C5A059]' : 'text-[#64748B]'
                    }`} />
                    <span className={`font-medium ${
                      role === 'student' ? 'text-[#0F172A]' : 'text-[#64748B]'
                    }`}>Student</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('parent')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      role === 'parent'
                        ? 'border-[#C5A059] bg-[#C5A059]/5'
                        : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                    }`}
                    data-testid="role-parent"
                  >
                    <Users className={`w-8 h-8 mx-auto mb-2 ${
                      role === 'parent' ? 'text-[#C5A059]' : 'text-[#64748B]'
                    }`} />
                    <span className={`font-medium ${
                      role === 'parent' ? 'text-[#0F172A]' : 'text-[#64748B]'
                    }`}>Parent</span>
                  </button>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div>
                  <Label htmlFor="name" className="text-[#0F172A]">Full Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="pl-11 h-12 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                      placeholder="Enter your full name"
                      data-testid="input-name"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-[#0F172A]">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-11 h-12 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                    placeholder="Enter your email"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-[#0F172A]">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-11 pr-11 h-12 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                    placeholder={mode === 'register' ? 'Create a password (min 6 chars)' : 'Enter your password'}
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Student-specific fields */}
              {mode === 'register' && role === 'student' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="school" className="text-[#0F172A]">School</Label>
                      <div className="relative mt-2">
                        <School className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                        <Input
                          id="school"
                          name="school"
                          type="text"
                          value={formData.school}
                          onChange={handleInputChange}
                          className="pl-11 h-12 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                          placeholder="Your school"
                          data-testid="input-school"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="year_group" className="text-[#0F172A]">Year Group</Label>
                      <select
                        id="year_group"
                        name="year_group"
                        value={formData.year_group}
                        onChange={handleInputChange}
                        className="w-full h-12 mt-2 px-3 rounded-md border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white text-[#0F172A]"
                        data-testid="select-year-group"
                      >
                        <option value="">Select year</option>
                        <option value="Year 7">Year 7</option>
                        <option value="Year 8">Year 8</option>
                        <option value="Year 9">Year 9</option>
                        <option value="Year 10">Year 10</option>
                        <option value="Year 11">Year 11</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="exam_board" className="text-[#0F172A]">Exam Board</Label>
                      <select
                        id="exam_board"
                        name="exam_board"
                        value={formData.exam_board}
                        onChange={handleInputChange}
                        className="w-full h-12 mt-2 px-3 rounded-md border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white text-[#0F172A]"
                        data-testid="select-exam-board"
                      >
                        <option value="">Select board</option>
                        <option value="AQA">AQA</option>
                        <option value="Edexcel">Edexcel</option>
                        <option value="OCR">OCR</option>
                        <option value="WJEC">WJEC</option>
                        <option value="iGCSE">iGCSE</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="tier" className="text-[#0F172A]">Tier</Label>
                      <select
                        id="tier"
                        name="tier"
                        value={formData.tier}
                        onChange={handleInputChange}
                        className="w-full h-12 mt-2 px-3 rounded-md border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white text-[#0F172A]"
                        data-testid="select-tier"
                      >
                        <option value="">Select tier</option>
                        <option value="Foundation">Foundation</option>
                        <option value="Higher">Higher</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Parent-specific fields */}
              {mode === 'register' && role === 'parent' && (
                <div>
                  <Label htmlFor="student_email" className="text-[#0F172A]">
                    Student's Email (Optional)
                  </Label>
                  <p className="text-xs text-[#64748B] mt-1 mb-2">
                    Link to your child's account. They must register first.
                  </p>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="student_email"
                      name="student_email"
                      type="email"
                      value={formData.student_email}
                      onChange={handleInputChange}
                      className="pl-11 h-12 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                      placeholder="student@example.com"
                      data-testid="input-student-email"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium"
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                  </span>
                ) : (
                  mode === 'login' ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E2E8F0]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#FFFCF5] text-[#64748B]">or continue with</span>
              </div>
            </div>

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full h-12 border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#0F172A] font-medium"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {mode === 'login' && (
              <p className="text-center text-sm text-[#64748B] mt-6">
                Don't have an account?{' '}
                <button 
                  onClick={() => setMode('register')} 
                  className="text-[#C5A059] hover:underline font-medium"
                >
                  Create one
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
